import {
  Button,
  Card,
  Image as AntImage,
  InputNumber,
  message,
  Progress,
  Space,
  Typography,
} from 'antd'
import {
  AssetVizCompartmentsDisplayState,
  AssetVizController,
  AssetVizHeatmapDisplayState,
  AssetVizMode,
  AssetVizSceneConfig,
  ClipConfig,
} from 'features/assetViz'
import { determineDefaultColorScaleThresholdNonCdf } from 'features/dataLayers'
import { useRolodexEdit } from 'features/fde'
import {
  useRolodexCache,
  useRolodexCachedEntries,
  useRolodexFileLS,
  useRolodexSignedURLs,
} from 'features/rolodex'
import { AssetVizCopy } from 'pages/reporting/components/AssetVizCopy'
import { useRautReportOps } from 'pages/reporting/reports/raut/RautReportProvider'
import { useUnitSystem } from 'pages/reporting/shared/contexts/UnitSystemContext'
import React, {
  FC,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import styled from 'styled-components'
import {
  GeckoAssetModel,
  GeckoComponent,
  GeckoComponentDataFrame,
  GeckoComponentModel,
  GeckoComponentRect,
  GeckoComponentShape,
  GeckoFeature,
  GeckoInspection,
  GeckoMarkupLayer,
  GeckoMarkupPrimitive,
  GeckoMeasurement,
  GeckoMeasurementRange,
  GeckoSource,
  GeckoVisualInspection,
  RolodexEntryType,
} from 'types'

const AntText = Typography.Text

const DEFAULT_ZOOM_FACTOR = 2.5
const DEFAULT_DISTANCE_PER_PAGE = 20

const StatusCard = styled(Card)`
  margin-bottom: 20px;
`

const ProgressInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin: 20px 0;
`

const ButtonContainer = styled(Space)`
  margin-bottom: 16px;
`

const ZoomControls = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  margin-bottom: 16px;
`

interface CaptureStatus {
  isCapturing: boolean
  message: string
}

interface DrawingImages {
  withHeatmap: string[]
  withoutHeatmap: string[]
  withIndicationsHeatmap: string[]
  withWeldsNoHeatmap: string[]
}

type DrawingMode =
  | 'heatmap'
  | 'no-heatmap'
  | 'indications'
  | 'welds-no-heatmap'
  | 'default'

export interface Indication {
  id: string
  title?: string
  selection?: {
    x: { min: { num: number }; max: { num: number }; unit: string }
    y: { min: { num: number }; max: { num: number }; unit: string }
  }
}

export interface AssetDrawingTabContentProps {
  inspection: GeckoInspection | GeckoVisualInspection
  inspectionId: string
  component: GeckoComponent
  componentModel: GeckoComponentModel
  thicknessCDF: GeckoComponentDataFrame | undefined
  markupLayers: readonly GeckoMarkupLayer[]
  indications: Indication[]
  assetModel: GeckoAssetModel | undefined
  heatmapSceneConfig: AssetVizSceneConfig | undefined
  noHeatmapSceneConfig: AssetVizSceneConfig | undefined
  metadataEntryId: string
  hasPhasedArrayInspection: boolean
  onDrawingFinish: (
    imagePath: string,
    imageKey:
      | 'heatmap'
      | 'no-heatmap'
      | 'heatmap-with-indications'
      | 'welds-no-heatmap',
    index: number,
    inspectionId: string,
    imageCaption?: string
  ) => Promise<void>
  onCaptureStatusChange?: (status: CaptureStatus) => void
  isLocked: boolean
  nominal: GeckoMeasurement | undefined
}

export const AssetDrawingTabContent: FC<AssetDrawingTabContentProps> = ({
  inspection,
  inspectionId,
  component,
  componentModel,
  thicknessCDF,
  markupLayers,
  indications,
  assetModel,
  heatmapSceneConfig,
  noHeatmapSceneConfig,
  metadataEntryId,
  hasPhasedArrayInspection,
  onDrawingFinish,
  onCaptureStatusChange,
  isLocked,
  nominal,
}) => {
  const { isLoading } = useRautReportOps()
  // Local viz ref for this tab
  const drawingVizRef = useRef<AssetVizController>(null)
  const cameraAspectRef = useRef<number>(-1)
  const isCurrentInspectionPAUT = useMemo(() => {
    return inspection.data.inspection_type === GeckoSource.PhasedArray.id
  }, [inspection])

  const hasThicknessData = !!thicknessCDF

  const { getDistanceUnit } = useUnitSystem()
  const distanceUnit = getDistanceUnit()

  // Local state for this tab
  const [zoomFactor, setZoomFactor] = useState(DEFAULT_ZOOM_FACTOR)
  const [distancePerPage, setDistancePerPage] = useState(
    DEFAULT_DISTANCE_PER_PAGE
  )
  const [loading, setLoading] = useState(false)
  const [currentConfig, setCurrentConfig] = useState(heatmapSceneConfig)
  const [temporaryMarkupIds, setTemporaryMarkupIds] = useState<string[]>([])
  const [currentMode, setCurrentMode] = useState<DrawingMode>('default')
  const [captureStatus, setCaptureStatus] = useState<CaptureStatus>({
    isCapturing: false,
    message: '',
  })
  const [existingDrawings, setExistingDrawings] = useState<DrawingImages>({
    withHeatmap: [],
    withoutHeatmap: [],
    withIndicationsHeatmap: [],
    withWeldsNoHeatmap: [],
  })

  const { rolodexUploadFile, rolodexDeleteFile } = useRolodexEdit()
  const rdCache = useRolodexCache()

  // Notify parent of capture status changes
  useEffect(() => {
    onCaptureStatusChange?.(captureStatus)
  }, [captureStatus, onCaptureStatusChange])

  // Scene configuration
  const [sceneConfig] = useState<AssetVizSceneConfig>(() => ({
    show3D: false,
    showRulers: true,
    heatmapDisplayState: AssetVizHeatmapDisplayState.Sharp,
    showFeatures: true,
    showCompass: false,
    showFeatureLabels: false,
    compartmentsDisplayState: AssetVizCompartmentsDisplayState.NominalPlates,
    showStructures: true,
    showHtmlOverlayDebugColors: false,
    showDebugStats: false,
    showFeatureDebugColors: false,
    showShellCourseDebugColors: false,
    showPlateFeatureDebugColors: false,
    showBinValues: false,
    showLegend: true,
    overrideCameraAspectRatio: cameraAspectRef,
    force11x7AspectRatio: false,
    stretchAssetToFitScreen: false,
    invertColors: false,
    heatmapOpacity: 1,
    assetOpacity: 1,
    clipConfig: new ClipConfig(),
    activePhotogrammetryModel: undefined,
    activePhotogrammetryTexture: undefined,
    setPhotogrammetryTextureLoading: undefined,
    showPhotogrammetryTexture: false,
    hasPhotogrammetryTextureUpdates: false,
  }))

  const [, setSelection] = useState<
    GeckoComponentShape | GeckoMarkupLayer | GeckoMarkupPrimitive | GeckoFeature
  >()

  // Color threshold
  const colorThreshold = useMemo(() => {
    if (!thicknessCDF) return undefined
    return determineDefaultColorScaleThresholdNonCdf(
      thicknessCDF.data.params.view,
      thicknessCDF.data.params.measurement_unit,
      nominal
        ? GeckoMeasurementRange.CreateFromStartEnd(
            GeckoMeasurement.Zero(),
            nominal
          )
        : undefined
    )
  }, [thicknessCDF, nominal])

  // Indication markups
  const indicationMarkups = useMemo(() => {
    return indications
      .filter((indication) => indication.selection)
      .map((indication, idx) => {
        const id = `temp-indication-markup-${idx}`
        const selection = indication.selection!

        const geometry = {
          x_start: selection.x.min.num,
          y_start: selection.y.min.num,
          x_end: selection.x.max.num,
          y_end: selection.y.max.num,
          x_measurement_unit: selection.x.unit,
          y_measurement_unit: selection.y.unit,
          type: 'rect',
        }

        return {
          id,
          type: RolodexEntryType.MARKUP_LAYER,
          data: {
            path: ['Indications'],
            color: '#FF0000',
            name: indication.title || `Indication ${idx + 1}`,
            category: 'indication',
            geometry,
            primitives: {
              [id]: {
                x_start: geometry.x_start,
                y_start: geometry.y_start,
                x_end: geometry.x_end,
                y_end: geometry.y_end,
                kind: 'rect',
              },
              [`title/${id}`]: {
                x: geometry.x_start + (geometry.x_end - geometry.x_start) / 2,
                y: geometry.y_start + (geometry.y_end - geometry.y_start) / 2,
                text: indication.title || `Indication ${idx + 1}`,
                kind: 'label',
              },
            },
          },
          tags: {
            ...component.tags,
            component: component.id,
            indication: indication.id,
          },
        }
      })
  }, [indications, component])

  useRolodexCachedEntries(indicationMarkups, rdCache)

  // Markup layer processing
  const { overlayMarkupIds, overlayRects, labelMarkupIds, weldMarkupIds } =
    useMemo(() => {
      const overlays: string[] = []
      const overlayRects: GeckoComponentRect[] = []
      const labels: string[] = []
      const welds: string[] = []

      markupLayers
        .filter((ml) => ml?.data?.path?.[0] === 'Report')
        .forEach((ml) => {
          if (ml.data.name === 'Scan Overlay') {
            overlays.push(ml.id)
            overlayRects.push(
              ...ml.data.rectPrimitives
                .map((gmr) =>
                  GeckoComponentRect.CreateFromCoords(
                    gmr.bounds.start,
                    gmr.bounds.end
                  )
                )
                .sort((a, b) => a.start.y.minus(b.start.y).num)
            )
          } else if (ml.data.name === 'Scan Labels') {
            labels.push(ml.id)
          } else if (
            hasPhasedArrayInspection &&
            (ml.data.name === 'Weld Scan Overlay' ||
              ml.data.name === 'Weld Scan Labels')
          ) {
            welds.push(ml.id)
          }
        })

      return {
        overlayMarkupIds: overlays,
        overlayRects,
        labelMarkupIds: labels,
        weldMarkupIds: welds,
      }
    }, [markupLayers, hasPhasedArrayInspection])

  // Distance-based chunking logic
  const vizChunks = useMemo(() => {
    if (!componentModel || !thicknessCDF || overlayRects.length === 0) {
      return []
    }

    const chunks: GeckoComponentRect[] = []
    const sortedScans = [...overlayRects].sort(
      (a, b) => a.start.y.minus(b.start.y).num
    )
    const unit = distanceUnit

    const componentBounds = sortedScans.reduce(
      (bounds, scan) => {
        return {
          minY: Math.min(bounds.minY, scan.start.y.toNumber(unit)),
          maxY: Math.max(bounds.maxY, scan.end.y.toNumber(unit)),
          minX: Math.min(bounds.minX, scan.start.x.toNumber(unit)),
          maxX: Math.max(bounds.maxX, scan.end.x.toNumber(unit)),
        }
      },
      {
        minY: sortedScans[0].start.y.toNumber(unit),
        maxY: sortedScans[0].end.y.toNumber(unit),
        minX: sortedScans[0].start.x.toNumber(unit),
        maxX: sortedScans[0].end.x.toNumber(unit),
      }
    )

    let currentPosition = componentBounds.minY

    while (currentPosition < componentBounds.maxY) {
      let chunkStart = currentPosition
      let chunkEnd = Math.min(
        currentPosition + distancePerPage,
        componentBounds.maxY
      )

      const intersectingScans = sortedScans.filter((scan) => {
        const scanStart = scan.start.y.toNumber(unit)
        const scanEnd = scan.end.y.toNumber(unit)
        return scanStart < chunkEnd && scanEnd > chunkStart
      })

      if (intersectingScans.length > 0) {
        const minScanStart = Math.min(
          ...intersectingScans.map((scan) => scan.start.y.toNumber(unit))
        )
        const maxScanEnd = Math.max(
          ...intersectingScans.map((scan) => scan.end.y.toNumber(unit))
        )

        chunkStart = Math.min(chunkStart, minScanStart)
        chunkEnd = Math.max(chunkEnd, maxScanEnd)
      }

      const chunkRect = GeckoComponentRect.CreateFromXYs(
        component.id,
        componentBounds.minX,
        chunkStart,
        componentBounds.maxX,
        chunkEnd,
        thicknessCDF.data.geometry.x.unit,
        unit
      )

      chunks.push(chunkRect)

      currentPosition = chunkEnd
    }

    return chunks
  }, [
    componentModel,
    thicknessCDF,
    overlayRects,
    distancePerPage,
    component,
    distanceUnit,
  ])

  // File management
  const { fileRefs, forceRefresh: refreshFileList } = useRolodexFileLS({
    entry_id: metadataEntryId,
    path: `asset-drawing/${inspectionId}/**`,
  })

  const { results: signedUrls, forceRefresh: refreshUrls } =
    useRolodexSignedURLs(
      fileRefs.map((ref) => ({
        entry_id: metadataEntryId,
        path: ref.path,
      }))
    )

  // Update existing drawings when signed URLs change
  useEffect(() => {
    const newDrawings: DrawingImages = {
      withHeatmap: [],
      withoutHeatmap: [],
      withIndicationsHeatmap: [],
      withWeldsNoHeatmap: [],
    }
    signedUrls.forEach((result) => {
      if (
        result.path?.includes('full-asset-heatmap') &&
        !result.path?.includes('indications')
      ) {
        newDrawings.withHeatmap.push(result.signed_url)
      } else if (result.path?.includes('full-asset-no-heatmap')) {
        newDrawings.withoutHeatmap.push(result.signed_url)
      } else if (result.path?.includes('full-asset-with-indications-heatmap')) {
        newDrawings.withIndicationsHeatmap.push(result.signed_url)
      } else if (result.path?.includes('full-asset-weld-scan-plan')) {
        newDrawings.withWeldsNoHeatmap.push(result.signed_url)
      }
    })
    setExistingDrawings(newDrawings)
  }, [signedUrls])

  // Capture single drawing with all three modes
  const captureAndSaveDrawing = useCallback(
    async (geometry: GeckoComponentRect, index: number) => {
      if (
        !drawingVizRef?.current ||
        !componentModel ||
        !component ||
        !distancePerPage
      ) {
        message.error('Unable to capture drawing - missing required data')
        return
      }

      // Calculate zoom-adjusted bounds for thickness and weld captures
      // Use componentModel units if we don't have thicknessCDF
      const xUnit =
        thicknessCDF?.data.geometry.x.unit ||
        componentModel.componentBounds.x.start.unit
      const yUnit =
        thicknessCDF?.data.geometry.y.unit ||
        componentModel.componentBounds.y.start.unit

      const width = geometry.x.delta.toNumber(xUnit)
      const height = geometry.y.delta.toNumber(yUnit)
      const centerX =
        (geometry.x.end.toNumber(xUnit) + geometry.x.start.toNumber(xUnit)) / 2
      const centerY =
        (geometry.y.end.toNumber(yUnit) + geometry.y.start.toNumber(yUnit)) / 2
      const scaledWidth = width / zoomFactor
      const scaledHeight = height / zoomFactor

      // Add 1% buffer to avoid clipping edges (mainly for welds)
      const bufferFactor = 1.01
      const bufferedWidth = scaledWidth * bufferFactor
      const bufferedHeight = scaledHeight * bufferFactor

      const newXStart = centerX - bufferedWidth / 2
      const newXEnd = centerX + bufferedWidth / 2
      const newYStart = centerY - bufferedHeight / 2
      const newYEnd = centerY + bufferedHeight / 2
      const bounds = GeckoComponentRect.CreateFromXYs(
        component.id,
        newXStart,
        newYStart,
        newXEnd,
        newYEnd,
        xUnit,
        yUnit
      )

      if (hasThicknessData) {
        // ===== CAPTURE HEATMAP (NO MARKUPS) =====
        setCaptureStatus({
          isCapturing: true,
          message: `Capturing heatmap drawing section ${index + 1}...`,
        })

        setCurrentMode('heatmap')
        setCurrentConfig(
          heatmapSceneConfig
            ? {
                ...heatmapSceneConfig,
                showRulers: false,
              }
            : undefined
        )

        await new Promise((resolve) => setTimeout(resolve, 1000))
        await drawingVizRef?.current.snapToFit(bounds)
        await drawingVizRef?.current.centerOn(bounds)
        await new Promise((resolve) => setTimeout(resolve, 500))

        const heatmapBlob = await drawingVizRef?.current.takeScreenshot()

        if (heatmapBlob) {
          const heatmapPath = `asset-drawing/${inspectionId}/full-asset-heatmap/${index}.png`
          await rolodexUploadFile(metadataEntryId, heatmapBlob, heatmapPath)
          await onDrawingFinish(heatmapPath, `heatmap`, index, inspectionId)
        }

        // ===== CAPTURE NO-HEATMAP (WITH STANDARD MARKUPS) =====
        setCaptureStatus({
          isCapturing: true,
          message: `Capturing drawing without heatmap drawing section ${index + 1}...`,
        })

        setCurrentMode('no-heatmap')
        setCurrentConfig(
          noHeatmapSceneConfig
            ? {
                ...noHeatmapSceneConfig,
                showRulers: true,
              }
            : undefined
        )

        await new Promise((resolve) => setTimeout(resolve, 1000))
        await drawingVizRef?.current.zoomToFit(bounds)
        await drawingVizRef?.current.centerOn(bounds)
        await new Promise((resolve) => setTimeout(resolve, 500))

        const noHeatmapBlob = await drawingVizRef?.current.takeScreenshot()
        if (noHeatmapBlob) {
          const noHeatmapPath = `asset-drawing/${inspectionId}/full-asset-no-heatmap/${index}.png`
          await rolodexUploadFile(metadataEntryId, noHeatmapBlob, noHeatmapPath)
          await onDrawingFinish(
            noHeatmapPath,
            `no-heatmap`,
            index,
            inspectionId
          )
        }

        // ===== CAPTURE INDICATIONS WITH HEATMAP =====
        setCaptureStatus({
          isCapturing: true,
          message: `Capturing drawing with indications and heatmap drawing section ${index + 1}...`,
        })

        setTemporaryMarkupIds(indicationMarkups.map((markup) => markup.id))

        setCurrentMode('indications')
        setCurrentConfig(
          heatmapSceneConfig
            ? {
                ...heatmapSceneConfig,
                showRulers: false,
              }
            : undefined
        )

        await new Promise((resolve) => setTimeout(resolve, 1000))
        await drawingVizRef.current.zoomToFit(bounds)
        await drawingVizRef.current.centerOn(bounds)
        await new Promise((resolve) => setTimeout(resolve, 500))

        const indicationsBlob = await drawingVizRef.current.takeScreenshot()
        if (indicationsBlob) {
          const indicationsPath = `asset-drawing/${inspectionId}/full-asset-with-indications-heatmap/${index}.png`
          await rolodexUploadFile(
            metadataEntryId,
            indicationsBlob,
            indicationsPath
          )
          await onDrawingFinish(
            indicationsPath,
            'heatmap-with-indications',
            index,
            inspectionId
          )
        }
      }

      // Capture welds if THIS inspection is PAUT and has weld markup
      if (isCurrentInspectionPAUT && weldMarkupIds.length > 0) {
        setCaptureStatus({
          isCapturing: true,
          message: `Capturing weld scan plan section ${index + 1}...`,
        })

        setCurrentMode('welds-no-heatmap')
        setCurrentConfig(
          noHeatmapSceneConfig
            ? {
                ...noHeatmapSceneConfig,
                showRulers: true,
              }
            : undefined
        )

        await new Promise((resolve) => setTimeout(resolve, 1000))
        await drawingVizRef.current.snapToFit(bounds)
        await drawingVizRef.current.centerOn(bounds)
        await new Promise((resolve) => setTimeout(resolve, 500))

        const weldScanBlob = await drawingVizRef.current.takeScreenshot()
        if (weldScanBlob) {
          const weldScanPath = `asset-drawing/${inspectionId}/full-asset-weld-scan-plan/${index}.png`
          await rolodexUploadFile(metadataEntryId, weldScanBlob, weldScanPath)
          await onDrawingFinish(
            weldScanPath,
            'welds-no-heatmap',
            index,
            inspectionId
          )
        }
      }

      // Reset to default state
      setCurrentMode('default')
      setTemporaryMarkupIds([])
      setCurrentConfig(heatmapSceneConfig)

      await refreshFileList()
      await refreshUrls()
      message.success('Asset drawings captured successfully')
    },
    [
      componentModel,
      component,
      hasThicknessData,
      thicknessCDF,
      zoomFactor,
      heatmapSceneConfig,
      noHeatmapSceneConfig,
      inspectionId,
      metadataEntryId,
      rolodexUploadFile,
      onDrawingFinish,
      indicationMarkups,
      isCurrentInspectionPAUT,
      weldMarkupIds,
      refreshFileList,
      refreshUrls,
      distancePerPage,
    ]
  )

  // Capture all drawings
  const captureAndSaveDrawings = useCallback(async () => {
    if (!drawingVizRef?.current || !componentModel || !component) {
      console.error('[AssetDrawings] Missing required data for capture')
      message.error('Unable to capture drawing - missing required data')
      return
    }

    setLoading(true)
    setCaptureStatus({
      isCapturing: true,
      message: 'Preparing to capture drawings...',
    })

    try {
      if (fileRefs.length > 0) {
        setCaptureStatus({
          isCapturing: true,
          message: 'Removing existing drawings...',
        })
        for (const ref of fileRefs) {
          await rolodexDeleteFile(metadataEntryId, ref.path)
        }
        await refreshFileList()
      }
      const screenshotTasks = vizChunks.map(
        (geometry, index) => () => captureAndSaveDrawing(geometry, index)
      )
      for (const task of screenshotTasks) {
        await task()
      }
    } catch (error) {
      console.error('Failed to capture asset drawings:', error)
      message.error('Failed to capture asset drawings')
    } finally {
      setCurrentMode('default')
      setTemporaryMarkupIds([])
      setLoading(false)
      setCaptureStatus({
        isCapturing: false,
        message: '',
      })
    }
  }, [
    componentModel,
    component,
    fileRefs,
    vizChunks,
    refreshFileList,
    rolodexDeleteFile,
    metadataEntryId,
    captureAndSaveDrawing,
  ])

  // Determine which markup layers to display
  const displayMarkupLayerIds = useMemo(() => {
    switch (currentMode) {
      case 'heatmap':
        return []
      case 'no-heatmap':
        return [...overlayMarkupIds, ...labelMarkupIds]
      case 'indications':
        return [...overlayMarkupIds, ...temporaryMarkupIds]
      case 'welds-no-heatmap':
        return weldMarkupIds
      case 'default':
      default:
        return [...overlayMarkupIds, ...labelMarkupIds]
    }
  }, [
    currentMode,
    overlayMarkupIds,
    labelMarkupIds,
    temporaryMarkupIds,
    weldMarkupIds,
  ])

  const filteredMarkupLayers = useMemo(() => {
    const idsSet = new Set(displayMarkupLayerIds)
    return [...markupLayers.filter((layer) => idsSet.has(layer.id))]
  }, [markupLayers, displayMarkupLayerIds])

  return (
    <>
      <AssetVizCopy
        assetVizRef={drawingVizRef}
        components={component ? [component] : []}
        componentDataFrames={thicknessCDF ? [thicknessCDF] : undefined}
        markupLayers={filteredMarkupLayers}
        assetModel={assetModel}
        getColorThresholdForComponent={() => colorThreshold}
        sceneConfig={currentConfig ?? sceneConfig}
        mode={AssetVizMode.Screenshot}
        onSelection={setSelection}
        offscreenRender={true}
      />

      <ZoomControls>
        <AntText>Zoom Factor:</AntText>
        <InputNumber
          min={0.1}
          max={5}
          step={0.1}
          value={zoomFactor}
          onChange={(value) => setZoomFactor(value || DEFAULT_ZOOM_FACTOR)}
          style={{ width: 120 }}
          addonAfter='×'
          disabled={isLocked}
        />
        <Button size='small' onClick={() => setZoomFactor(DEFAULT_ZOOM_FACTOR)}>
          Reset
        </Button>
      </ZoomControls>
      <ZoomControls>
        <AntText>Distance Per Page (in {distanceUnit.name}):</AntText>
        <InputNumber
          min={0}
          step={1}
          value={distancePerPage}
          onChange={(value) => {
            if (value) {
              setDistancePerPage(value)
            }
          }}
          disabled={isLocked}
        />
        <Button
          size='small'
          onClick={() => setDistancePerPage(DEFAULT_DISTANCE_PER_PAGE)}>
          Reset
        </Button>
      </ZoomControls>

      <ButtonContainer>
        <Button
          onClick={captureAndSaveDrawings}
          loading={loading}
          disabled={isLocked || isLoading}>
          {existingDrawings.withHeatmap.length > 0 ||
          existingDrawings.withoutHeatmap.length > 0 ||
          existingDrawings.withIndicationsHeatmap.length > 0 ||
          existingDrawings.withWeldsNoHeatmap.length > 0
            ? 'Recapture Asset Drawings'
            : 'Capture Asset Drawings'}
        </Button>
      </ButtonContainer>

      {loading && (
        <StatusCard>
          <ProgressInfo>
            <div>
              <strong>Status:</strong> {captureStatus.message}
            </div>
            <Progress
              percent={captureStatus.isCapturing ? 50 : 100}
              status='active'
            />
          </ProgressInfo>
        </StatusCard>
      )}

      {(existingDrawings.withHeatmap.length > 0 ||
        existingDrawings.withoutHeatmap.length > 0 ||
        existingDrawings.withIndicationsHeatmap.length > 0 ||
        existingDrawings.withWeldsNoHeatmap.length > 0) && (
        <>
          {existingDrawings.withHeatmap.length > 0 && (
            <Card title='Asset Drawing with Heatmap'>
              {existingDrawings.withHeatmap.map(
                (url: string, index: number) => (
                  <div key={index}>
                    <AntImage
                      src={url}
                      alt={`Asset Drawing with Heatmap ${index}`}
                      style={{
                        width: '100%',
                        maxWidth: '800px',
                        marginBottom: '1rem',
                      }}
                    />
                  </div>
                )
              )}
            </Card>
          )}
          {existingDrawings.withoutHeatmap.length > 0 && (
            <Card title='Asset Drawing without Heatmap'>
              {existingDrawings.withoutHeatmap.map(
                (url: string, index: number) => (
                  <div key={index}>
                    <AntImage
                      src={url}
                      alt={`Asset Drawing without Heatmap ${index}`}
                      style={{
                        width: '100%',
                        maxWidth: '800px',
                        marginBottom: '1rem',
                      }}
                    />
                  </div>
                )
              )}
            </Card>
          )}
          {existingDrawings.withIndicationsHeatmap.length > 0 && (
            <Card title='Asset Drawing with Indications'>
              {existingDrawings.withIndicationsHeatmap.map(
                (url: string, index: number) => (
                  <div key={index}>
                    <AntImage
                      src={url}
                      alt={`Asset Drawing with Indications ${index}`}
                      style={{
                        width: '100%',
                        maxWidth: '800px',
                        marginBottom: '1rem',
                      }}
                    />
                  </div>
                )
              )}
            </Card>
          )}
          {existingDrawings.withWeldsNoHeatmap.length > 0 && (
            <Card title='Asset Drawing - Weld Scan Plan'>
              {existingDrawings.withWeldsNoHeatmap.map(
                (url: string, index: number) => (
                  <div key={index}>
                    <AntImage
                      src={url}
                      alt={`Asset Drawing - Weld Scan Plan ${index}`}
                      style={{
                        width: '100%',
                        maxWidth: '800px',
                        marginBottom: '1rem',
                      }}
                    />
                  </div>
                )
              )}
            </Card>
          )}
        </>
      )}
    </>
  )
}
